import { useState } from 'react';

interface Product {
  id: string;
  name: string;
  price: number;
  currency: string;
  image: string;
  supplierName: string;
  supplierCity: string;
  warehouseCity: string;
  wechat: string;
  email: string;
  shippingDays: string;
}

interface OrderModalProps {
  product: Product;
  onClose: () => void;
}

type OrderStep = 'form' | 'contact';

export default function OrderModal({ product, onClose }: OrderModalProps) {
  const [step, setStep] = useState<OrderStep>('form');
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({
    buyerName: '',
    buyerPhone: '',
    buyerEmail: '',
    buyerWechat: '',
    address: '',
    note: '',
    payMethod: 'wechat',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [contactCopied, setContactCopied] = useState<string | null>(null);

  const total = (product.price * qty).toFixed(2);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.buyerName.trim()) errs.buyerName = '请输入姓名';
    if (!form.buyerPhone.trim()) errs.buyerPhone = '请输入联系电话';
    if (!form.address.trim()) errs.address = '请输入收货地址';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const PAY_LABELS: Record<string, string> = {
    wechat: '微信支付',
    alipay: '支付宝',
    card: '银行卡/信用卡',
    paypal: 'PayPal',
  };

  const handleOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const body = new URLSearchParams();
    body.append('product', product.name);
    body.append('quantity', String(qty));
    body.append('total', `${product.currency}${total}`);
    body.append('buyer_name', form.buyerName);
    body.append('buyer_phone', form.buyerPhone);
    body.append('email', form.buyerEmail);
    body.append('buyer_wechat', form.buyerWechat);
    body.append('address', form.address);
    body.append('payment_method', PAY_LABELS[form.payMethod] || form.payMethod);
    body.append('note', form.note);
    try {
      await fetch('https://readdy.ai/api/form/d7d688nsch5bvrijfccg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch { /* silent */ }
    setTimeout(() => {
      setSubmitting(false);
      setStep('contact');
    }, 1400);
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setContactCopied(key);
      setTimeout(() => setContactCopied(null), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-8 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl my-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-800 text-base">
            {step === 'form' ? '立即下单' : '联系卖家'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {step === 'form' ? (
          <form onSubmit={handleOrder} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
            {/* Product Summary */}
            <div className="flex gap-3 bg-gray-50 rounded-2xl p-3">
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 leading-snug">{product.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{product.warehouseCity} · {product.shippingDays}发货</p>
                <p className="text-rose-600 font-bold text-base mt-1">{product.currency}{product.price}</p>
              </div>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">购买数量</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer"
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <span className="text-lg font-bold text-gray-800 w-8 text-center">{qty}</span>
                <button
                  type="button"
                  onClick={() => setQty(qty + 1)}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-rose-100 text-rose-600 hover:bg-rose-200 cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                </button>
                <span className="text-sm text-gray-400 ml-2">合计：<strong className="text-rose-600">{product.currency}{total}</strong></span>
              </div>
            </div>

            {/* Buyer Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">收货人姓名 *</label>
                <input
                  type="text"
                  placeholder="您的姓名"
                  value={form.buyerName}
                  onChange={(e) => setForm({ ...form, buyerName: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
                />
                {errors.buyerName && <p className="text-xs text-red-500 mt-1">{errors.buyerName}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">联系电话 *</label>
                <input
                  type="tel"
                  placeholder="手机号码"
                  value={form.buyerPhone}
                  onChange={(e) => setForm({ ...form, buyerPhone: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
                />
                {errors.buyerPhone && <p className="text-xs text-red-500 mt-1">{errors.buyerPhone}</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">收货地址 *</label>
              <input
                type="text"
                placeholder="详细收货地址"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
              />
              {errors.address && <p className="text-xs text-red-500 mt-1">{errors.address}</p>}
            </div>

            {/* Buyer Contact for Exchange */}
            <div className="bg-rose-50 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-semibold text-rose-700 flex items-center gap-1.5">
                <i className="ri-exchange-line"></i> 您的联系方式（卖家确认后可互换）
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">微信号</label>
                  <input
                    type="text"
                    placeholder="您的微信号"
                    value={form.buyerWechat}
                    onChange={(e) => setForm({ ...form, buyerWechat: e.target.value })}
                    className="w-full border border-rose-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-rose-400 bg-white transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">邮箱</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={form.buyerEmail}
                    onChange={(e) => setForm({ ...form, buyerEmail: e.target.value })}
                    className="w-full border border-rose-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-rose-400 bg-white transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">支付方式</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'wechat', label: '微信支付', icon: 'ri-wechat-pay-line', color: 'text-green-600' },
                  { key: 'alipay', label: '支付宝', icon: 'ri-alipay-line', color: 'text-sky-500' },
                  { key: 'card', label: '银行卡/信用卡', icon: 'ri-bank-card-line', color: 'text-gray-600' },
                  { key: 'paypal', label: 'PayPal', icon: 'ri-paypal-line', color: 'text-sky-700' },
                ].map((pm) => (
                  <button
                    key={pm.key}
                    type="button"
                    onClick={() => setForm({ ...form, payMethod: pm.key })}
                    className={`flex items-center gap-2 px-3 py-3 rounded-xl border text-xs font-medium transition-all cursor-pointer ${form.payMethod === pm.key ? 'border-rose-400 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  >
                    <i className={`${pm.icon} text-lg ${form.payMethod === pm.key ? 'text-rose-500' : pm.color}`}></i>
                    {pm.label}
                  </button>
                ))}
              </div>
              {form.payMethod === 'paypal' && (
                <div className="mt-3 bg-sky-50 border border-sky-100 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-paypal-line text-sky-600 text-lg flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-sky-700 leading-relaxed">
                    下单后，卖家将通过 PayPal 向您发送付款请求。支持全球主要货币，自动换算，安全有保障。
                  </p>
                </div>
              )}
            </div>

            {/* Note */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">备注（可选）</label>
              <textarea
                placeholder="尺码、颜色、特殊要求等..."
                value={form.note}
                onChange={(e) => {
                  if (e.target.value.length <= 500) setForm({ ...form, note: e.target.value });
                }}
                rows={2}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors resize-none"
              />
              <p className="text-xs text-gray-300 text-right">{form.note.length}/500</p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-rose-600 to-amber-500 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> 提交订单中...
                </span>
              ) : (
                <span><i className="ri-shopping-bag-line mr-2"></i>确认下单 · {product.currency}{total}</span>
              )}
            </button>
          </form>
        ) : (
          /* Contact Exchange Step */
          <div className="px-6 py-6 space-y-5">
            {/* Success Banner */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
              </div>
              <div>
                <p className="font-semibold text-green-800 text-sm">订单提交成功！</p>
                <p className="text-xs text-green-600 mt-0.5">卖家将在{product.shippingDays}内确认并发货，您可通过以下方式联系卖家</p>
              </div>
            </div>

            {/* Seller Contact */}
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-3 flex items-center gap-1.5">
                <i className="ri-user-star-line text-amber-500"></i> 卖家联系方式
              </p>
              <div className="space-y-3">
                {product.wechat && (
                  <div className="flex items-center justify-between bg-green-50 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-full">
                        <i className="ri-wechat-line text-green-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">微信号</p>
                        <p className="text-sm font-semibold text-gray-800">{product.wechat}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(product.wechat, 'wechat')}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-full text-xs font-medium cursor-pointer hover:bg-green-700 transition-all whitespace-nowrap"
                    >
                      <i className={contactCopied === 'wechat' ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                      {contactCopied === 'wechat' ? '已复制' : '复制'}
                    </button>
                  </div>
                )}
                {product.email && (
                  <div className="flex items-center justify-between bg-blue-50 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-blue-100 rounded-full">
                        <i className="ri-mail-line text-blue-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">邮箱</p>
                        <p className="text-sm font-semibold text-gray-800">{product.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(product.email, 'email')}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-full text-xs font-medium cursor-pointer hover:bg-blue-700 transition-all whitespace-nowrap"
                    >
                      <i className={contactCopied === 'email' ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                      {contactCopied === 'email' ? '已复制' : '复制'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Seller Info */}
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl p-4">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gray-200">
                <img
                  src={`https://public.readdy.ai/ai/img_res/11807578f4fd296e02dbf9dd720b75b5.jpg${product.id}&orientation=squarish`}
                  alt={product.supplierName}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{product.supplierName}</p>
                <p className="text-xs text-gray-400">{product.supplierCity} · 本地供应商</p>
              </div>
            </div>

            {/* Payment reminder */}
            <div className={`border rounded-2xl p-4 ${form.payMethod === 'paypal' ? 'bg-sky-50 border-sky-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-xs flex items-start gap-2 ${form.payMethod === 'paypal' ? 'text-sky-700' : 'text-amber-700'}`}>
                <i className={`mt-0.5 flex-shrink-0 ${form.payMethod === 'paypal' ? 'ri-paypal-line' : 'ri-information-line'}`}></i>
                {form.payMethod === 'paypal'
                  ? '您选择了 PayPal 支付。请联系卖家，卖家将通过 PayPal 向您发送付款请求，支持全球主要货币，安全有保障。'
                  : '请通过以上方式联系卖家确认订单详情、尺码颜色及付款方式。交易请注意安全，建议使用平台推荐的支付方式。'}
              </p>
            </div>

            <button
              onClick={onClose}
              className="w-full py-3 bg-gray-800 text-white rounded-xl font-semibold text-sm hover:bg-gray-700 transition-all cursor-pointer whitespace-nowrap"
            >
              完成
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
